import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ShieldCheck, ShieldAlert, Pencil, Plus, Trash2, BadgeCheck } from "lucide-react";

export function certStatus(expiry) {
  if (!expiry) return { key: "na", label: "No expiry", cls: "bg-slate-100 text-slate-500 border-slate-200" };
  const days = Math.ceil((new Date(expiry) - new Date()) / 86400000);
  if (days < 0) return { key: "expired", label: `Expired ${Math.abs(days)}d ago`, cls: "bg-red-100 text-red-700 border-red-200" };
  if (days <= 30) return { key: "soon", label: `${days}d left`, cls: "bg-amber-100 text-amber-700 border-amber-200" };
  return { key: "valid", label: `${days}d left`, cls: "bg-lime-100 text-lime-700 border-lime-200" };
}

function overall(certs) {
  const st = (certs || []).map((c) => certStatus(c.expiry).key);
  if (st.includes("expired")) return { label: "Not compliant", cls: "bg-red-100 text-red-700 border-red-200", icon: ShieldAlert };
  if (st.includes("soon")) return { label: "Renewal due", cls: "bg-amber-100 text-amber-700 border-amber-200", icon: ShieldAlert };
  return { label: "Compliant", cls: "bg-lime-100 text-lime-700 border-lime-200", icon: ShieldCheck };
}

export default function CrewCompliance({ employees, jobs, onChanged }) {
  const [edit, setEdit] = useState(null); // employee being edited
  const [certs, setCerts] = useState([]);
  const [saving, setSaving] = useState(false);

  const openEdit = (e) => { setEdit(e); setCerts((e.certifications || []).map((c) => ({ ...c }))); };
  const addCert = () => setCerts((c) => [...c, { name: "", number: "", expiry: "" }]);
  const setCert = (i, k, v) => setCerts((c) => c.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const removeCert = (i) => setCerts((c) => c.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/employees/${edit.id}`, { name: edit.name, role: edit.role, color: edit.color, certifications: certs.filter((c) => c.name.trim()) });
      toast.success("Licences updated"); setEdit(null); onChanged();
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  return (
    <div>
      <h2 className="text-xl font-semibold tracking-tight text-slate-900 font-heading mb-4">Your Crew</h2>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {employees.map((e) => {
          const count = jobs.filter((j) => j.employee_id === e.id && j.status !== "cancelled").length;
          const ov = overall(e.certifications);
          const OvIcon = ov.icon;
          return (
            <div key={e.id} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`crew-${e.id}`}>
              <div className="flex items-center gap-3">
                <span className="h-10 w-10 rounded-full flex items-center justify-center font-semibold text-slate-900 shrink-0" style={{ background: e.color }}>{e.name.charAt(0)}</span>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-slate-900 truncate">{e.name}</p>
                  <p className="text-xs text-slate-500 truncate">{e.role} · {count} jobs</p>
                </div>
                <Badge className={`border gap-1 shrink-0 ${ov.cls}`} data-testid={`crew-status-${e.id}`}><OvIcon className="h-3 w-3" />{ov.label}</Badge>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-slate-900 shrink-0" data-testid={`manage-crew-${e.id}`} onClick={() => openEdit(e)}><Pencil className="h-4 w-4" /></Button>
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100 space-y-1.5">
                {(e.certifications || []).length === 0 ? (
                  <p className="text-xs text-slate-400 italic">No licences recorded.</p>
                ) : e.certifications.map((c, i) => {
                  const st = certStatus(c.expiry);
                  return (
                    <div key={i} className="flex items-center justify-between gap-2 text-sm" data-testid={`crew-cert-${e.id}-${i}`}>
                      <span className="flex items-center gap-1.5 text-slate-700 min-w-0"><BadgeCheck className="h-3.5 w-3.5 text-slate-300 shrink-0" /><span className="truncate">{c.name}</span></span>
                      <div className="flex items-center gap-2 shrink-0">
                        {c.expiry && <span className="text-xs text-slate-400">{c.expiry}</span>}
                        <Badge className={`border text-[10px] ${st.cls}`}>{st.label}</Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="crew-edit-dialog">
          <DialogHeader><DialogTitle className="font-heading">{edit?.name} — Licences & Renewals</DialogTitle><DialogDescription>Track certificates and expiry dates to keep this pilot job-ready.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            {certs.map((c, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end border border-slate-100 rounded-lg p-2" data-testid={`cert-row-${i}`}>
                <div className="col-span-5"><Label className="text-[10px] uppercase text-slate-400">Licence / certificate</Label><Input value={c.name} onChange={(ev) => setCert(i, "name", ev.target.value)} className="h-8 mt-1" placeholder="e.g. RePL" data-testid={`cert-name-${i}`} /></div>
                <div className="col-span-3"><Label className="text-[10px] uppercase text-slate-400">Number</Label><Input value={c.number} onChange={(ev) => setCert(i, "number", ev.target.value)} className="h-8 mt-1" /></div>
                <div className="col-span-3"><Label className="text-[10px] uppercase text-slate-400">Expiry</Label><Input type="date" value={c.expiry || ""} onChange={(ev) => setCert(i, "expiry", ev.target.value)} className="h-8 mt-1" data-testid={`cert-expiry-${i}`} /></div>
                <div className="col-span-1"><Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600" onClick={() => removeCert(i)}><Trash2 className="h-4 w-4" /></Button></div>
              </div>
            ))}
            <Button variant="outline" size="sm" className="w-full border-dashed" onClick={addCert} data-testid="add-cert-btn"><Plus className="h-4 w-4 mr-1" /> Add licence</Button>
            <Button onClick={save} disabled={saving} className="w-full bg-lime-400 hover:bg-lime-300 text-slate-900 font-semibold" data-testid="save-crew-btn">{saving ? "Saving…" : "Save"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
